/**
 * Explore's queries for one view: cards + line chart (totals and series of
 * every enabled metric), the donut (selected metric by the view's dimension)
 * and one page of the table. A widget query carries at most eight metrics,
 * so cards and table columns are asked in groups (the sort metric rides in
 * every table group so each returns the same rows) and merged back here.
 */

import { useEffect, useState } from 'react';

import {
  reportsApi,
  type CompareSetting,
  type Filter,
  type RangeSetting,
  type WidgetResult,
} from '../../reports/reports-api.ts';

const METRICS_PER_QUERY = 8;
export const TABLE_PAGE = 50;
const DONUT_SLICES = 8;

export interface ExploreQuery {
  organisationId: string;
  clientId: string;
  timezone: string;
  range: RangeSetting;
  compare: CompareSetting;
  filters: Filter[];
  cards: string[];
  selected: string;
  donut: string | null;
  /** The chart interval ("Date Interval"); null leaves it to the range length. */
  granularity: 'day' | 'week' | 'month' | null;
  table: {
    dimension: string;
    metrics: string[];
    search: string;
    sort: { metric: string; direction: 'asc' | 'desc' };
    page: number;
  };
}

export interface ExploreData {
  cards: WidgetResult | null;
  donut: WidgetResult | null;
  table: WidgetResult | null;
  loading: boolean;
  error: string | null;
}

function groups(metrics: string[], keep?: string): string[][] {
  const rest = metrics.filter((metric) => metric !== keep);
  const size = keep ? METRICS_PER_QUERY - 1 : METRICS_PER_QUERY;
  const out: string[][] = [];
  for (let at = 0; at < rest.length || out.length === 0; at += size)
    out.push([...(keep ? [keep] : []), ...rest.slice(at, at + size)]);
  return out;
}

/** Joins grouped answers into one result: metrics side by side, series and rows merged by bucket / label. */
function merge(parts: WidgetResult[]): WidgetResult | null {
  const [first, ...others] = parts;
  if (!first) return null;
  const failed = parts.find((part) => part.state !== 'OK');
  if (failed) return failed;
  const merged: WidgetResult = structuredClone(first);
  for (const other of others) {
    for (const metric of other.metrics)
      if (!merged.metrics.some((item) => item.key === metric.key)) merged.metrics.push(metric);
    merged.series.forEach((point, index) =>
      Object.assign(point.values, other.series[index]?.values),
    );
    merged.compare_series?.forEach((point, index) =>
      Object.assign(point.values, other.compare_series?.[index]?.values),
    );
    merged.rows?.forEach((row) => {
      const match = other.rows?.find((candidate) => candidate.label === row.label);
      Object.assign(row.values, match?.values);
      if (row.compare && match?.compare) Object.assign(row.compare, match.compare);
    });
  }
  return merged;
}

export function useExploreData(query: ExploreQuery): ExploreData {
  const [data, setData] = useState<ExploreData>({
    cards: null,
    donut: null,
    table: null,
    loading: true,
    error: null,
  });
  const signature = JSON.stringify(query);
  useEffect(() => {
    let cancelled = false;
    setData((previous) => ({ ...previous, loading: true }));
    const cardGroups = groups(query.cards);
    const tableGroups = groups(query.table.metrics, query.table.sort.metric);
    const search: Filter[] = query.table.search.trim()
      ? [{ dimension: query.table.dimension, op: 'contains', values: [query.table.search.trim()] }]
      : [];
    const widgets = [
      ...cardGroups.map((metrics, index) => ({
        id: `cards-${index}`,
        ...(query.granularity ? { granularity: query.granularity } : {}),
        source: 'ga4',
        metrics,
        filters: query.filters,
      })),
      ...(query.donut
        ? [
            {
              id: 'donut',
              source: 'ga4',
              metrics: [query.selected],
              dimension: query.donut,
              filters: query.filters,
              limit: DONUT_SLICES,
              sort: { metric: query.selected, direction: 'desc' },
            },
          ]
        : []),
      ...tableGroups.map((metrics, index) => ({
        id: `table-${index}`,
        source: 'ga4',
        metrics,
        dimension: query.table.dimension,
        filters: [...query.filters, ...search],
        sort: query.table.sort,
        limit: TABLE_PAGE,
        offset: query.table.page * TABLE_PAGE,
      })),
    ];
    reportsApi
      .query(query.organisationId, {
        business_unit_id: query.clientId,
        mode: 'LIVE',
        timezone: query.timezone,
        range: query.range,
        compare: query.compare,
        widgets,
      })
      .then(({ results }) => {
        if (cancelled) return;
        const pick = (prefix: string) => results.filter((result) => result.id.startsWith(prefix));
        setData({
          cards: merge(pick('cards-')),
          donut: results.find((result) => result.id === 'donut') ?? null,
          table: merge(pick('table-')),
          loading: false,
          error: null,
        });
      })
      .catch((problem: unknown) => {
        if (cancelled) return;
        setData({
          cards: null,
          donut: null,
          table: null,
          loading: false,
          error:
            problem instanceof Error ? problem.message : 'Google Analytics could not be loaded.',
        });
      });
    return () => {
      cancelled = true;
    };
    // The signature captures every input that changes the answer.
  }, [signature]);
  return data;
}
